import { useEffect } from 'react';
import EDDatePicker from '../../BaseComponents/DatePicker/EDDatePicker';
import EDBox from '../../BaseComponents/EDBox/EDBox';
import { EDTextEdit } from '../../BaseComponents/TextEdit/EDTextEdit';
import { IBusinessDataFormContext } from '../../Features/Delsystem/_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';

interface IProps {
  formContext: IBusinessDataFormContext<any, unknown>;
  avlID?: string;
  attributeName: string;
  disabled?: boolean;
  value?: string;
  date: string;
  setDate: React.Dispatch<React.SetStateAction<string>>;
  time: string;
  setTime: React.Dispatch<React.SetStateAction<string>>;
  dodsDatum?: string;
}
const EDDateAndTime: React.FC<IProps> = props => {
  useEffect(() => {
    const newValue = `${props.date.replaceAll('-', '')}${props.time.replace(
      ':',
      ''
    )}`;
    if (
      props.date.replaceAll('-', '').length > 7 ||
      props.time.replaceAll(':', '').length > 3
    ) {
      props.formContext?.update([
        {
          operation: 'Values',
          associationName: 'Avlidna',
          values: {
            id: props.avlID,
            [props.attributeName]: newValue,
          },
        },
      ]);
    } else {
      props.formContext?.update([
        {
          operation: 'Values',
          associationName: 'Avlidna',
          values: {
            id: props.avlID,
            [props.attributeName]: undefined,
          },
        },
      ]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.date, props.time]);

  const handleDateKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setTimeout(() => {
        if (!props.date) {
          const now = new Date();
          const formattedDate = now.toISOString().split('T')[0];
          props.setDate(formattedDate);
        } else {
          let [year, month, day] = props.date.split('-').map(Number);
          const dateObj = new Date(year, month - 1, day);

          if (event.key === 'ArrowDown') {
            dateObj.setDate(dateObj.getDate() - 1);
          } else if (event.key === 'ArrowUp') {
            dateObj.setDate(dateObj.getDate() + 1);
          }

          const newYear = dateObj.getFullYear();
          const newMonth = String(dateObj.getMonth() + 1).padStart(2, '0');
          const newDay = String(dateObj.getDate()).padStart(2, '0');
          const formattedDate = `${newYear}-${newMonth}-${newDay}`;
          props.setDate(formattedDate);
        }
      }, 100);
    }
  };

  const handleDateChange = (value: string) => {
    let filteredValue = value.replace(/[^0-9-]/g, '');
    let numbersCount = value.replace(/[^0-9]/g, '').length;
    if (numbersCount > 8) return;

    if (filteredValue.endsWith('-')) {
      const beforeHyphen = filteredValue.slice(-3, -1);
      if (!/^\d{2}$/.test(beforeHyphen)) {
        filteredValue = filteredValue.slice(0, -1);
      }
    }
    props.setDate(filteredValue);
  };

  const handleTimeKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setTimeout(() => {
        if (!props.time) {
          const now = new Date();
          props.setTime(
            now.toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })
          );
        } else {
          let [hours, minutes] = props.time.split(':').map(Number);

          if (event.key === 'ArrowDown') {
            minutes--;
            if (minutes < 0) {
              minutes = 59;
              hours = hours === 0 ? 23 : hours - 1;
            }
          } else if (event.key === 'ArrowUp') {
            minutes++;
            if (minutes > 59) {
              minutes = 0;
              hours = hours === 23 ? 0 : hours + 1;
            }
          }

          const formattedTime = `${String(hours).padStart(2, '0')}:${String(
            minutes
          ).padStart(2, '0')}`;
          props.setTime(formattedTime);
        }
      }, 80);
    }
  };

  const handleTimeChange = (event: string) => {
    let value = event.replace(':', '');

    const hours = value.substring(0, 2);
    const minutes = value.substring(2, 4) === '' ? '00' : value.substring(2, 4);

    const isValidHours = /^(0?[0-9]|1[0-9]|2[0-3])$/.test(hours);
    const isValidMinutes = /^[0-5][0-9]?$/.test(minutes);

    if (isValidHours && isValidMinutes) {
      if (value.length === 1 && Number(value) > 2) {
        props.setTime('0' + value);
      } else if (value.length <= 2) {
        props.setTime(value);
      } else if (value.length === 3 || value.length === 4) {
        props.setTime(`${value.substring(0, 2)}:${value.substring(2)}`);
      }
    } else if (value === '') {
      props.setTime('');
    }
  };

  const handleTimeBlur = () => {
    const unformattedTime = props.time.replaceAll(':', '');
    if (unformattedTime.length === 1) {
      props.setTime('0' + unformattedTime + ':00');
    } else if (unformattedTime.length === 2) {
      props.setTime(unformattedTime + ':00');
    } else if (unformattedTime.length === 3) {
      props.setTime(
        unformattedTime.substring(0, 2) +
          ':' +
          unformattedTime.substring(2, 3) +
          '0'
      );
    }
  };

  return (
    <EDBox sx={{ display: 'flex', alignItems: 'center' }}>
      <EDBox marginRight={0.5} width="100%">
        <EDDatePicker
          marker={props.date.length > 7}
          fullWidth
          label="Datum"
          value={
            props.value
              ? `${props.value.slice(0, 4)}-${props.value.slice(
                  4,
                  6
                )}-${props.value.slice(6, 8)}`
              : props.date
          }
          disabled={props.disabled}
          onKeyDown={handleDateKeyDown}
          onChange={handleDateChange}
          helperText={
            props.date.length === 0 && props.time.length > 0
              ? 'Värde krävs'
              : props.date.replaceAll('-', '').length !== 8 &&
                props.date.replaceAll('-', '').length > 0
              ? 'År, månad, och dag behöver anges'
              : Number(props.dodsDatum) >
                  Number(props.date.replaceAll('-', '')) &&
                props.date.replaceAll('-', '').length === 8
              ? 'Får inte vara tidigare än dödsdatumet'
              : ''
          }
          testId={'DATUM_' + props.attributeName}
        />
      </EDBox>
      <EDBox marginLeft={0.5} width="100%">
        <EDTextEdit
          marker={props.time.length > 3}
          fullWidth
          label="Tid"
          value={
            props.value?.length === 12
              ? `${props.value.slice(8, 10)}:${props.value.slice(10, 12)}`
              : props.time
          }
          disabled={props.disabled}
          onKeyDown={handleTimeKeyDown}
          onChange={handleTimeChange}
          onBlur={handleTimeBlur}
          testId={'TID_' + props.attributeName}
        />
      </EDBox>
    </EDBox>
  );
};

export default EDDateAndTime;
